/**
 * Seed Stage — Stage 5b: Seed consolidation warning integration.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.10
 *
 * The seedConsolidator auto-corrects tier/skill/subSkill mismatches at load time,
 * which means the schema stage never sees these issues. This stage surfaces those
 * corrections as proper ValidationIssues so they appear in the governance UI.
 *
 * Parses consolidation warnings (TIER-LOCK, SKILL-LOCK, SUBSKILL-LOCK) and maps
 * them to question IDs. Results are cached for performance.
 *
 * Issue kinds:
 * - SEED_TIER_AUTOCORRECTED: blocker — tier in seed JSON doesn't match taxonomy
 * - SEED_TIER_INVALID: blocker — tier was missing or non-numeric
 * - SEED_TIER_OUT_OF_RANGE: blocker — tier was > 16
 * - SEED_SKILL_AUTOCORRECTED: coach — skill didn't match taxonomy
 * - SEED_SUBSKILL_AUTOCORRECTED: coach — subSkill didn't match taxonomy
 */

import type { ValidationIssue } from '../issueContract';
import { blocker, coach } from '../issueContract';
import { getConsolidationReport } from '../../../content';

// ─── Cache ────────────────────────────────────────────────────────────────────

let _cachedWarningMap: Map<string, ValidationIssue[]> | null = null;

/**
 * Invalidate the cached warning map (for tests or hot-reload).
 */
export function invalidateSeedWarningCache(): void {
  _cachedWarningMap = null;
}

// ─── Warning Parser ───────────────────────────────────────────────────────────

/**
 * Parse consolidation warnings into a map of questionId → ValidationIssue[].
 * Cached after first call.
 */
function getWarningMap(): Map<string, ValidationIssue[]> {
  if (_cachedWarningMap) return _cachedWarningMap;
  const map = new Map<string, ValidationIssue[]>();
  const report = getConsolidationReport();
  for (const warning of report.warnings) {
    const parsed = parseWarning(warning);
    if (!parsed) continue;
    const existing = map.get(parsed.questionId) || [];
    existing.push(parsed.issue);
    map.set(parsed.questionId, existing);
  }
  _cachedWarningMap = map;
  return map;
}
interface ParsedWarning {
  questionId: string;
  issue: ValidationIssue;
}

/**
 * Parse a single consolidation warning string into a ValidationIssue.
 *
 * Warning formats from seedConsolidator:
 * - "SEED TIER-LOCK: Question {id} has invalid tier ({rawTier}), auto-corrected to taxonomy tier {taxonomyTier} for family {familyId}"
 * - "SEED TIER-LOCK: Question {id} has out-of-range tier {rawTier}, auto-corrected to taxonomy tier {taxonomyTier} for family {familyId}"
 * - "SEED TIER-LOCK: Question {id} (family {familyId}) has tier {rawTier} but taxonomy defines tier {taxonomyTier}. Auto-corrected to {taxonomyTier}."
 * - "SEED SKILL-LOCK: Question {id} (family {familyId}) has skill "{skill}" but taxonomy defines "{taxonomySkill}". Auto-corrected."
 * - "SEED SUBSKILL-LOCK: Question {id} (family {familyId}) has subSkill "{subSkill}" but taxonomy defines "{taxonomySubSkill}". Auto-corrected."
 */
function parseWarning(warning: string): ParsedWarning | null {
  // ── TIER-LOCK: invalid tier ──
  const invalidTierMatch = warning.match(/SEED TIER-LOCK: Question (\S+) has invalid tier \(([^)]*)\), auto-corrected to taxonomy tier (\d+) for family (\S+)/);
  if (invalidTierMatch) {
    const [, questionId, rawTier, taxonomyTier, familyId] = invalidTierMatch;
    return {
      questionId: questionId!,
      issue: blocker('seed', 5, 'SEED_TIER_INVALID', warning, {
        field: 'tier',
        expected: taxonomyTier,
        actual: rawTier,
        questionId: questionId!,
        familyId: familyId!,
        suggestion: 'Fix the tier value in the seed JSON to match the taxonomy definition'
      })
    };
  }

  // ── TIER-LOCK: out-of-range ──
  const outOfRangeMatch = warning.match(/SEED TIER-LOCK: Question (\S+) has out-of-range tier (\d+), auto-corrected to taxonomy tier (\d+) for family (\S+)/);
  if (outOfRangeMatch) {
    const [, questionId, rawTier, taxonomyTier, familyId] = outOfRangeMatch;
    return {
      questionId: questionId!,
      issue: blocker('seed', 5, 'SEED_TIER_OUT_OF_RANGE', warning, {
        field: 'tier',
        expected: taxonomyTier,
        actual: rawTier,
        questionId: questionId!,
        familyId: familyId!,
        suggestion: 'Tier must be 1-16. Fix the tier value in the seed JSON.'
      })
    };
  }

  // ── TIER-LOCK: mismatch ──
  const tierMismatchMatch = warning.match(/SEED TIER-LOCK: Question (\S+) \(family (\S+)\) has tier (\d+) but taxonomy defines tier (\d+)/);
  if (tierMismatchMatch) {
    const [, questionId, familyId, rawTier, taxonomyTier] = tierMismatchMatch;
    return {
      questionId: questionId!,
      issue: blocker('seed', 5, 'SEED_TIER_AUTOCORRECTED', `Seed has tier ${rawTier} but taxonomy defines tier ${taxonomyTier} — auto-corrected at load time`, {
        field: 'tier',
        expected: taxonomyTier,
        actual: rawTier,
        questionId: questionId!,
        familyId: familyId!,
        suggestion: `Update tier from ${rawTier} to ${taxonomyTier} in the seed JSON to fix this permanently`
      })
    };
  }

  // ── SKILL-LOCK ──
  const skillMatch = warning.match(/SEED SKILL-LOCK: Question (\S+) \(family (\S+)\) has skill "([^"]+)" but taxonomy defines "([^"]+)"/);
  if (skillMatch) {
    const [, questionId, familyId, rawSkill, taxonomySkill] = skillMatch;
    return {
      questionId: questionId!,
      issue: coach('seed', 5, 'SEED_SKILL_AUTOCORRECTED', `Seed has skill "${rawSkill}" but taxonomy defines "${taxonomySkill}" — auto-corrected at load time`, {
        field: 'skill',
        expected: taxonomySkill,
        actual: rawSkill,
        questionId: questionId!,
        familyId: familyId!,
        severity: 60,
        suggestion: `Update skill from "${rawSkill}" to "${taxonomySkill}" in the seed JSON`
      })
    };
  }

  // ── SUBSKILL-LOCK ──
  const subSkillMatch = warning.match(/SEED SUBSKILL-LOCK: Question (\S+) \(family (\S+)\) has subSkill "([^"]+)" but taxonomy defines "([^"]+)"/);
  if (subSkillMatch) {
    const [, questionId, familyId, rawSubSkill, taxonomySubSkill] = subSkillMatch;
    return {
      questionId: questionId!,
      issue: coach('seed', 5, 'SEED_SUBSKILL_AUTOCORRECTED', `Seed has subSkill "${rawSubSkill}" but taxonomy defines "${taxonomySubSkill}" — auto-corrected at load time`, {
        field: 'subSkill',
        expected: taxonomySubSkill,
        actual: rawSubSkill,
        questionId: questionId!,
        familyId: familyId!,
        severity: 55,
        suggestion: `Update subSkill from "${rawSubSkill}" to "${taxonomySubSkill}" in the seed JSON`
      })
    };
  }
  return null;
}

// ─── Stage Entry Point ────────────────────────────────────────────────────────

/**
 * Run seed consolidation warning check for a single question.
 * Returns ValidationIssue[] for any tier/skill/subSkill auto-corrections
 * that were applied to this question during seed loading.
 */
export function runSeedStage(question: {
  id?: string;
}): ValidationIssue[] {
  const qId = question.id ?? 'UNKNOWN';
  const warningMap = getWarningMap();
  return warningMap.get(qId) ?? [];
}